// Angular Core Modules
import { Component, inject, Input } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';

// Third Party Modules
import { Store } from '@ngrx/store';

// Application Modules
import { EpTableComponent, EPTableConfig, LoadingSpinnerComponent } from '@ep/shared';

import { selectConsumersSortedByName } from '@app/resources/ngrx/selectors';
import { MemberModuleActions } from '@app/areas/members/store';
import { ConsumerTableDisplay } from '@app/areas/members/models/consumer-table-display.model';
import { mapToConsumerTableDisplays } from '@app/areas/members/utils';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';

// Utilities and Operators
import { map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { MemberLoadingService } from '../../services/member-loading.service';
import { ListPagination, ListPaginationService } from '@app/resources/services';
import { PortalTheme } from '@app/@shared/models';

const DEFAULT_ROWS = 10 as const;

@Component({
  selector: 'app-members-table',
  standalone: true,
  imports: [EpTableComponent, AsyncPipe, LoadingSpinnerComponent, NgIf],
  template: `
    <div *ngIf="!(isLoading$ | async)" class="p-4">
      <app-ep-table
        [config]="config"
        [data]="members$ | async"
        (pageChange)="onPageChange($event)"
        data-cy="members-table"
      />
    </div>
    <app-loading-spinner *ngIf="isLoading$ | async" [message]="'Loading...'" class="text-center" />
  `,
})
export class MembersTableComponent {
  private readonly store = inject(Store);
  private readonly loadingService = inject(MemberLoadingService);
  private readonly cssValidatorService = inject(CssValidatorService);
  private readonly listPagination = inject(ListPaginationService);

  private readonly first$ = new BehaviorSubject<number>(0);
  private readonly rows$ = new BehaviorSubject<number>(DEFAULT_ROWS);

  public isLoading$ = this.loadingService.isLoading$;

  @Input() public theme: PortalTheme | null = null;
  protected config!: EPTableConfig<ConsumerTableDisplay>;

  ngOnInit(): void {
    this.setCSSCustomProperties();
    this.restorePagination();
    this.initializeTableConfig();
  }

  protected onPageChange(pagination: ListPagination): void {
    this.first$.next(pagination.first);
    this.rows$.next(pagination.rows);
    this.listPagination.write(pagination, DEFAULT_ROWS);
  }

  private restorePagination(): void {
    const { first, rows } = this.listPagination.read(DEFAULT_ROWS);

    this.first$.next(first);
    this.rows$.next(rows);
  }

  private initializeTableConfig(): void {
    this.config = {
      header: 'members',
      pagination: {
        rows: DEFAULT_ROWS,
        showCurrentPageReport: true,
      },
      first$: this.first$,
      rows$: this.rows$,
      columns: [
        {
          header: 'First Name',
          field: 'NameFirst',
          columnType: 'string',
          defaultValue: 'VALUED',
        },
        {
          header: 'Last Name',
          field: 'NameLast',
          columnType: 'string',
          defaultValue: 'CUSTOMER',
        },
        {
          header: 'Last Seen',
          field: 'LastSeenDateTime',
          columnType: 'date',
          defaultValue: null,
        },
        {
          header: 'Total Visits',
          field: 'TotalVisits',
          columnType: 'numerical',
          defaultValue: 0,
        },
        {
          header: 'Total Spend',
          field: 'TotalSpend',
          columnType: 'price',
          defaultValue: 0,
        },
        {
          header: 'Average Spend',
          field: 'AverageSpend',
          columnType: 'price',
          defaultValue: 0,
        },
        {
          header: 'Visit Frequency',
          field: 'VisitFrequency',
          columnType: 'string',
          defaultValue: 'N/A',
        },
      ],
      style: {
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      globalFilterFields: ['NameFirst', 'NameLast'],
      actions: {
        rowClick: (member: ConsumerTableDisplay) => {
          this.loadingService.setLoadingStatus(true);
          this.store.dispatch(
            MemberModuleActions.navigateExistingMember({
              payload: { consumerId: member.ConsumerId },
            })
          );
        },
      },
    };
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('MembersTableComponent');
    }
  }

  protected members$ = this.store.select(selectConsumersSortedByName).pipe(map(mapToConsumerTableDisplays));
}
